import { BaseElement } from 'base';
import { bus } from 'bus';
import { SEARCH_TOPIC } from 'search-topic';

const TOPIC = 'mobile-menu:active';
const DESKTOP = '(min-width: 1200px)';
const LOCK_CLASS = 'mobile-menu-open';
const HISTORY_KEY = 'mobileMenu';
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'summary',
  "[tabindex]:not([tabindex='-1'])"
]
  .map(selector => `[data-mobile-menu-panel] ${selector}`)
  .join(', ');

const backTraversal = { pending: false, settledBy: null };
let restoredEntry = false;

const historyEnabled = () => !window.Shopify?.designMode;
const currentState = () =>
  history.state && typeof history.state === 'object' ? history.state : {};
const markedPanel = () => currentState()[HISTORY_KEY];
const withoutHash = href => href.split('#')[0];

class MobileMenu extends BaseElement {
  #panel = null;
  #scrim = null;
  #toggle = null;
  #active = null;
  #frame = 0;
  #restoreFocus = true;
  #fromHistory = false;
  #localeOpen = false;
  #lastHref = '';

  onConnect() {
    this.#panel = this.q('[data-mobile-menu-panel]');
    if (!this.#panel) return;
    this.#scrim = this.q('[data-mobile-menu-scrim]');
    this.#toggle = this.q(`[aria-controls="${CSS.escape(this.#panel.id)}"]`);
    this.#lastHref = location.href;

    this.cleanup(() => cancelAnimationFrame(this.#frame));
    this.subscribe(TOPIC, this.#render);
    this.subscribe(`${this.#panel.id}-views:active`, state =>
      this.#showView(state?.value)
    );
    this.subscribe('locale-selector:active', value => {
      this.#localeOpen = value != null;
    });
    this.subscribeLive('cart-drawer:active', value => {
      if (value) this.#closeOwn(false);
    });
    this.subscribeLive('slide-cart:active', value => {
      if (value != null) this.#closeOwn(false);
    });
    this.subscribeLive(SEARCH_TOPIC, value => {
      if (value) this.#closeOwn(false);
    });

    this.listen(this, 'click', this.#onClick);
    this.listen(this, 'keydown', this.#onKeydown);
    this.listen(document, 'pointerdown', this.#onDocumentPointerDown);
    this.listen(document, 'keydown', this.#onDocumentKeydown);
    this.listen(window, 'resize', this.#measure);
    this.listen(window, 'pageshow', this.#measure);
    this.listen(window, 'popstate', this.#onPopState);
    this.listen(window.matchMedia(DESKTOP), 'change', event => {
      if (event.matches) this.#closeOwn(false);
    });

    this.#restoreFromHistory();
  }

  onDisconnect() {
    if (!this.#owns()) return;
    if (historyEnabled() && markedPanel() === this.#panel.id) {
      this.#unmarkEntry();
    }
    this.#publishFromHistory(null);
  }

  #owns() {
    return this.#panel != null && this.#active === this.#panel.id;
  }

  #render(active) {
    const wasMine = this.#owns();
    this.#active = active;
    const mine = this.#owns();
    const restoreFocus = this.#restoreFocus;
    this.#restoreFocus = true;

    this.#panel.hidden = !mine;
    if (this.#scrim) this.#scrim.hidden = !mine;
    this.#toggle?.setAttribute('aria-expanded', String(mine));
    this.dataset.open = String(mine);

    if (mine) {
      document.documentElement.classList.add(LOCK_CLASS);
      this.#measure();
      this.#measureNextFrame();
    } else if (active == null) {
      document.documentElement.classList.remove(LOCK_CLASS);
    }

    if (
      wasMine &&
      !mine &&
      restoreFocus &&
      this.contains(document.activeElement)
    ) {
      this.#toggle?.focus();
    }

    this.#syncHistory(wasMine, mine, active);
  }

  #syncHistory(wasMine, mine, active) {
    if (this.#fromHistory || !historyEnabled()) return;
    const marked = markedPanel();

    if (!wasMine && mine) {
      if (backTraversal.pending || marked === this.#panel.id) return;
      this.#markEntry(marked != null);
    } else if (wasMine && active == null && marked === this.#panel.id) {
      if (backTraversal.pending) return;
      if (restoredEntry) {
        this.#unmarkEntry();
        return;
      }
      backTraversal.pending = true;
      history.back();
    }
  }

  #markEntry(takeOver) {
    const state = { ...currentState(), [HISTORY_KEY]: this.#panel.id };
    this.#lastHref = location.href;
    if (takeOver) {
      history.replaceState(state, '');
      return;
    }
    history.pushState(state, '');
    restoredEntry = false;
  }

  #unmarkEntry() {
    const state = { ...currentState() };
    delete state[HISTORY_KEY];
    history.replaceState(state, '');
  }

  #publishFromHistory(value) {
    this.#fromHistory = true;
    bus.publish(TOPIC, value);
    this.#fromHistory = false;
  }

  #onPopState(event) {
    if (!historyEnabled()) return;
    const previousHref = this.#lastHref;
    this.#lastHref = location.href;
    const entryIsMine = markedPanel() === this.#panel.id;

    if (backTraversal.pending || backTraversal.settledBy === event) {
      backTraversal.pending = false;
      backTraversal.settledBy = event;
      if (this.#owns() && !entryIsMine) this.#markEntry(markedPanel() != null);
      return;
    }

    if (event.state == null && this.#isFragmentStep(previousHref)) return;

    if (this.#owns() && !entryIsMine) {
      this.#publishFromHistory(null);
    } else if (
      !this.#owns() &&
      entryIsMine &&
      !window.matchMedia(DESKTOP).matches
    ) {
      this.#publishFromHistory(this.#panel.id);
    }
  }

  #isFragmentStep(previousHref) {
    return (
      location.href !== previousHref &&
      withoutHash(location.href) === withoutHash(previousHref)
    );
  }

  #restoreFromHistory() {
    if (!historyEnabled() || this.#owns()) return;
    if (markedPanel() !== this.#panel.id) return;
    if (window.matchMedia(DESKTOP).matches) {
      this.#unmarkEntry();
      return;
    }
    restoredEntry = true;
    this.#publishFromHistory(this.#panel.id);
  }

  #showView(value) {
    if (typeof value !== 'string') return;
    if (!this.q(`[data-mobile-menu-view="${CSS.escape(value)}"]`)) return;
    this.qa('[data-mobile-menu-view]').forEach(view => {
      view.hidden = view.dataset.mobileMenuView !== value;
    });
  }

  #measure() {
    if (!this.#owns()) return;
    this.style.setProperty(
      '--mobile-menu-top',
      `${this.#panel.getBoundingClientRect().top}px`
    );
  }

  #measureNextFrame() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#measure();
    });
  }

  #closeOwn(restoreFocus = true) {
    if (!this.#owns()) return;
    this.#restoreFocus = restoreFocus;
    bus.publish(TOPIC, null);
  }

  #onClick(event) {
    if (!this.#toggle?.contains(event.target)) return;
    bus.publish(TOPIC, this.#owns() ? null : this.#panel.id);
  }

  #onDocumentPointerDown(event) {
    if (!this.#owns() || this.#localeOpen) return;
    if (this.contains(event.target) && event.target !== this.#scrim) return;
    if (event.target.closest?.('dialog, a[href]')) return;
    this.#closeOwn();
  }

  #onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.#owns() || this.#localeOpen) return;
    if (event.target.closest?.('dialog')) return;
    this.#closeOwn();
  }

  #onKeydown(event) {
    if (event.key !== 'Tab' || !this.#owns() || !this.#toggle) return;

    const items = [
      this.#toggle,
      ...this.qa(FOCUSABLE).filter(item => item.getClientRects().length > 0)
    ];
    const first = items[0];
    const last = items[items.length - 1];
    const current = document.activeElement;

    if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('mobile-menu'))
  customElements.define('mobile-menu', MobileMenu);
